<template>
  <div id="chartDiv"></div>
</template>

<script>
import * as am4core from '@amcharts/amcharts4/core'
import * as am4charts from '@amcharts/amcharts4/charts'
import am4themesAnimated from '@amcharts/amcharts4/themes/animated'
import { mapActions, mapGetters } from 'vuex'

am4core.useTheme(am4themesAnimated)

export default {
  name: 'Chart',
  data () {
    return {
      /** @type {Object} Crée un set de couleur pour le dégradé des barres de résultats */
      colorSet: new am4core.ColorSet(),
      /**
       * @typedef {Object} ChartOptions
       * @property {Object} chart - Initie le graphique
       * @property {Object} cursor - Permet les effets de survol sur la cases au centre du graphique
       * @property {Object} sliderButton - Image du bouton de zoom
       * @property {Object} xAxis - Crée les légendes autour du graphique
       * @property {Object} xAxis.axis - Objet de AmChart qui porte la configuration
       * @property {Object} xAxis.label - Texte et options du texte de la légende
       * @property {Object} xAxis.renderer - Effets sur le texte de la légende
       * @property {Object} yAxis - Crée les barres de résultats
       * @property {Object} yAxis.axis - Objet de AmChart qui porte la configuration
       * @property {Object} yAxis.label - Texte au centre du graphique
       * @property {Object} yAxis.renderer - Effets sur les barres de résultats
       * @type {ChartOptions}
       */
      chart: {
        chart: null,
        cursor: null,
        sliderButton: null,
        xAxis: {
          axis: null,
          label: null,
          renderer: null
        },
        yAxis: {
          axis: null,
          label: null,
          renderer: null
        }
      },
      /**
       * @type {Object} Objet représentant la version graphique des résultats des différentes itérations
       * */
      series: null,
      /**
       * @typedef {Object} SettingsChart
       * @property {Object} iteration - Options sur les itérations
       * @property {Object} iteration.start - Itération de départ
       * @property {Object} iteration.end - Nombre d'itération max
       * @property {Object} iteration.current - Itération actuelle
       * @property {Object} radius - Options pour la taille du graphique
       * @property {Object} radius.inner - Rayon interne (cercle avec le label Y)
       * @property {Object} radius.externe - Rayon externe (avant le cercle des domaines)
       * @property {Object} radius.value - Rayon interne des labels autour du graphique
       * @property {Object} radius.value - Rayon interne des labels autour du graphique
       * @property {String} labelColor - Couleur du label au centre du graphique
       * @property {Object} values - Options pour les valeurs du graphique
       * @property {Object} values.min - Options pour les valeurs du graphique
       * @property {Object} values.min.value - Valeur minimale que peuvent prendre les résultats
       * @property {Object} values.min.color - Couleur minimale pour le gradient de couleur
       * @property {Object} values.max - Options pour les valeurs du graphique
       * @property {Object} values.max.value - Valeur maximale que peuvent prendre les résultats
       * @property {Object} values.max.color - Couleur maximale pour le gradient de couleur
       * @property {Object} skills - Options autour des compétences
       * @property {Object} skills.maxWidth - Taille maximale du label
       * @property {Object} thresholds - Options autour des seuils
       * @property {Object} thresholds.colors - Options autour des couleurs des seuils
       * @property {Object} thresholds.colors.max - Couleur pour le seuil maximal
       * @property {Object} thresholds.colors.min - Couleur pour le seuil minimal
       * @property {Object} thresholds.size - Largeur du cercle des seuils
       * @type {SettingsChart}
       */
      settings: {
        iteration: {
          start: 1,
          end: null,
          current: null
        },
        radius: {
          inner: 35,
          externe: 65,
          value: 80
        },
        labelColor: '#307373',
        values: {
          max: {
            value: 100,
            color: '#48648f'
          },
          min: {
            value: 0,
            color: '#8db9ff'
          }
        },
        skills: {
          maxWidth: 150
        },
        thresholds: {
          colors: {
            max: '#47A4A9',
            min: '#D77E75'
          },
          size: 3
        }
      },
      /**
       * @typedef {Object} SlidersSettings
       * @property {Object} container - Conteneur qui stocke les sliders en bas du grapgique
       * @property {Object} iteration - Slider qui gère le passage d'itération
       * @property {Object} deZoomButton - Bouton permettant de revenir au zoom 0
       * @property {Object} angle - Slider qui gère l'ouverture du radar
       * @type {SlidersSettings}
       */
      sliders: {
        container: null,
        iteration: null,
        deZoomButton: null,
        angle: null
      },
      /**
       * @typedef {Object} ThresholdChart
       * @property {Object} min - Seuil mini
       * @property {Object} max - Seuil maxi
       * @type {ThresholdChart}
       */
      thresholds: {
        min: null,
        max: null
      }
    }
  },
  computed: {
    ...mapGetters('UserQuestionnaire', [
      'finishedIterations',
      'resultNumber',
      'results',
      'threshold'
    ])
  },
  beforeDestroy () {
    this.chart.chart.dispose()
  },
  async mounted () {
    await this.loadThreshold()

    /** Initialisation du graphique */
    this.chart.chart = am4core.create('chartDiv', am4charts.RadarChart)
    this.chart.chart.hiddenState.properties.opacity = 0

    this.chart.chart.startAngle = 270 - 180
    this.chart.chart.endAngle = 270 + 180
    this.chart.chart.forceGap = true

    // Padding autour du radar
    this.chart.chart.padding(0, 100, 0, 100)
    // Rayon du centre à l'extérieur du radar
    this.chart.chart.radius = am4core.percent(this.settings.radius.externe)
    // Rayon du centre à l'intérieur du radar
    this.chart.chart.innerRadius = am4core.percent(this.settings.radius.inner)

    /** Initialisation du conteneur droit du radar */
    this.chart.chart.topAxesContainer.layout = 'horizontal'
    this.chart.chart.topAxesContainer.padding(0, 0, 0, 0)

    /**
     * Création et modification des options du bouton de dézoom à droite
     * Le bouton apparaît quand on a cliqué sur un domaine
     * On l'insère dans le conteneur droit du radar
     */
    this.sliders.deZoomButton = this.chart.chart.zoomOutButton
    this.sliders.deZoomButton.disabled = true
    // Bouton de dézoom désactivé ci-dessus, options du bouton ci-dessous
    // this.sliders.deZoomButton.dx = 0
    // this.sliders.deZoomButton.dy = 0
    // this.sliders.deZoomButton.marginBottom = 15
    // this.sliders.deZoomButton.cursorOverStyle = am4core.MouseCursorStyle.pointer
    // this.sliders.deZoomButton.parent = this.chart.chart.topAxesContainer
    // this.sliders.deZoomButton.readerTitle = 'Zoom arrière'
    // this.sliders.deZoomButton.icon.disabled = true
    // this.chart.sliderButton = this.sliders.deZoomButton.createChild(am4core.Image)
    // this.chart.sliderButton.href = require('@/assets/img/zoom-out.svg')
    // this.chart.sliderButton.width = 20
    // this.chart.sliderButton.height = 20

    /**
     * Initialisation de l'axe des X = les compétences autour du radar
     */
    this.chart.xAxis.axis = this.chart.chart.xAxes.push(new am4charts.CategoryAxis())
    this.chart.xAxis.axis.renderer.grid.template.location = 0
    // On attache la clé 'skillShortName' dans l'objet qui contient les résultats en fonction des compétences
    this.chart.xAxis.axis.dataFields.category = 'skillShortName'

    this.chart.xAxis.renderer = this.chart.xAxis.axis.renderer
    this.chart.xAxis.label = this.chart.xAxis.renderer.labels.template
    this.chart.xAxis.label.location = 0.5
    this.chart.xAxis.label.radius = 28
    this.chart.xAxis.label.relativeRotation = 90
    this.chart.xAxis.label.wrap = true
    this.chart.xAxis.label.maxWidth = this.settings.skills.maxWidth
    this.chart.xAxis.label.fontSize = 18

    this.chart.xAxis.renderer.fontSize = 11
    this.chart.xAxis.renderer.minGridDistance = 10
    this.chart.xAxis.renderer.grid.template.radius = -25
    // opacité des barres du radar qui partent du centre et qui vont vers l'extérieur
    this.chart.xAxis.renderer.grid.template.strokeOpacity = 0.3
    this.chart.xAxis.renderer.grid.template.interactionsEnabled = false

    this.chart.xAxis.renderer.ticks.template.disabled = true
    // Pour ajouter une couleur de fond le passer à false car valeur par défaut à true
    this.chart.xAxis.renderer.axisFills.template.disabled = true
    this.chart.xAxis.renderer.line.disabled = true

    this.chart.xAxis.renderer.tooltipLocation = 0.5
    this.chart.xAxis.axis.tooltip.defaultState.properties.opacity = 0

    /** Initialisation de l'axe des Y = valeurs au centre du radar */
    this.chart.yAxis.axis = this.chart.chart.yAxes.push(new am4charts.ValueAxis())
    this.chart.yAxis.axis.min = this.settings.values.min.value
    this.chart.yAxis.axis.max = this.settings.values.max.value
    this.chart.yAxis.axis.tooltip.defaultState.properties.opacity = 0

    /** Création et affichage du seuil du seuil minimum */
    this.thresholds.min = this.chart.yAxis.axis.axisRanges.create()
    this.thresholds.min.value = this.threshold.low
    this.thresholds.min.grid.stroke = am4core.color(this.settings.thresholds.colors.min)
    this.thresholds.min.grid.strokeWidth = this.settings.thresholds.size
    this.thresholds.min.label.inside = true
    this.thresholds.min.label.fill = this.thresholds.min.grid.stroke
    this.thresholds.min.label.verticalCenter = 'bottom'

    /** Création et affichage du seuil du seuil maximum */
    this.thresholds.max = this.chart.yAxis.axis.axisRanges.create()
    this.thresholds.max.value = this.threshold.high
    this.thresholds.max.grid.stroke = am4core.color(this.settings.thresholds.colors.max)
    this.thresholds.max.grid.strokeWidth = this.settings.thresholds.size
    this.thresholds.max.label.inside = true
    this.thresholds.max.label.fill = this.thresholds.max.grid.stroke
    this.thresholds.max.label.verticalCenter = 'bottom'

    /** Initialisation du label au centre du radar */
    this.chart.yAxis.label = this.chart.chart.radarContainer.createChild(am4core.Label)
    this.chart.yAxis.label.horizontalCenter = 'middle'
    this.chart.yAxis.label.verticalCenter = 'middle'
    this.chart.yAxis.label.fill = am4core.color(this.settings.labelColor)

    this.chart.yAxis.renderer = this.chart.yAxis.axis.renderer
    this.chart.yAxis.renderer.axisFills.template.disabled = true
    this.chart.yAxis.renderer.ticks.template.disabled = true
    // Permet de ne pas avoir de cercle intermédiaire au centre du radar et de n'avoir que les seuils
    this.chart.yAxis.renderer.minGridDistance = 100
    this.chart.yAxis.renderer.labels.template.disabled = true
    // opacité des cercles du radar
    this.chart.yAxis.renderer.grid.template.strokeOpacity = 1

    /** Création des séries */
    this.series = this.chart.chart.series.push(new am4charts.RadarColumnSeries())
    // Utilisé pour faire pointer les barres de résultat pour déterminer leur angle de départ
    this.series.columns.template.width = am4core.percent(this.settings.radius.value)
    // Border des barres de résultat
    this.series.columns.template.strokeOpacity = 0
    this.series.dataFields.valueY = 'value' + this.settings.iteration.current
    this.series.dataFields.categoryX = 'skillShortName'
    this.series.dataFields.complet = 'skillCompletName'
    this.series.tooltipText = '{complet}: {valueY.value}%'
    // Permet la modification du template du tooltip
    this.series.tooltip.getFillFromObject = false
    this.series.tooltip.background.fill = am4core.color('#CDE0FA')
    this.series.tooltip.label.fill = am4core.color('#000000')

    /**
     * Initie les barres de valeur
     * Détermine les valeurs de départ et de fin
     * Le dégradé de couleur entre les deux
     */
    this.series.heatRules.push({
      target: this.series.columns.template,
      property: 'fill',
      minValue: this.settings.values.min.value,
      maxValue: this.settings.values.max.value,
      min: am4core.color(this.settings.values.min.color),
      max: am4core.color(this.settings.values.max.color),
      dataField: 'valueY'
    })

    /** Initie le curseur = effet de survol sur les cases centrales du radar */
    this.chart.cursor = new am4charts.RadarCursor()
    this.chart.chart.cursor = this.chart.cursor
    this.chart.cursor.behavior = 'zoomX'

    this.chart.cursor.xAxis = this.chart.xAxis.axis
    this.chart.cursor.innerRadius = am4core.percent(this.settings.radius.inner)
    this.chart.cursor.lineY.disabled = true

    this.chart.cursor.lineX.fillOpacity = 0.2
    this.chart.cursor.lineX.fill = am4core.color('#000000')
    this.chart.cursor.lineX.strokeOpacity = 0
    this.chart.cursor.fullWidthLineX = true

    /** Création d'un container pour les sliders du bas */
    this.sliders.container = this.chart.chart.createChild(am4core.Container)
    this.sliders.container.layout = 'vertical'
    this.sliders.container.padding(0, 0, 0, 0)
    this.sliders.container.width = am4core.percent(100)

    /**
     * Création du slider pour les itérations
     * Construit unique s'il y a plus d'une itération
     */
    if (this.resultNumber > 1) {
      this.sliders.iteration = this.sliders.container.createChild(am4core.Slider)
      this.sliders.iteration.events.on('rangechanged', () => {
        this.updateRadarData(this.settings.iteration.start + Math.round(
          this.sliders.iteration.start * (this.settings.iteration.end - this.settings.iteration.start))
        )
      })
      this.sliders.iteration.orientation = 'horizontal'
      this.sliders.iteration.exportable = false
      this.sliders.iteration.tooltipText = 'Parcourir les itérations'
      this.sliders.iteration.showSystemTooltip = false
      this.sliders.iteration.background.fill = am4core.color(this.settings.thresholds.colors.max)
    }

    /** Création du slider pour l'angle du radar */
    this.sliders.angle = this.sliders.container.createChild(am4core.Slider)
    this.sliders.angle.start = 1
    this.sliders.angle.exportable = false
    this.sliders.angle.tooltipText = 'Changer l\'angle du radar.'
    this.sliders.angle.showSystemTooltip = false
    this.sliders.angle.background.fill = am4core.color(this.settings.thresholds.colors.max)
    this.sliders.angle.events.on('rangechanged', () => {
      let start = this.sliders.angle.start

      this.chart.chart.startAngle = 270 - start * 179 - 1
      this.chart.chart.endAngle = 270 + start * 179 + 1

      this.chart.yAxis.axis.renderer.axisAngle = this.chart.chart.startAngle
    })

    /** Injection des data dans le graphique */
    this.chart.chart.data = await this.generateRadarData()

    /** Graphique complètement chargé */
    this.$emit('loaded')
  },
  methods: {
    ...mapActions('UserQuestionnaire', [
      'loadThreshold'
    ]),
    /**
     * Adapte l'itération en fonction de la valeur du slider pour les itération.
     *
     * @param iteration {Number}
     */
    updateRadarData (iteration) {
      if (this.settings.iteration.current !== iteration) {
        this.settings.iteration.current = iteration
        this.chart.yAxis.label.text = this.finishedIterations[this.settings.iteration.current - 1]
          .finishedAt
        this.series.dataFields.valueY = 'value' + this.settings.iteration.current
        this.chart.chart.invalidateRawData()
      }
    },
    /**
     * Génère le tableau des données en fonction du tableau de départ.
     *
     * @typedef {Object} skillData
     * @property {String} skillShortName - Nom court de la compétence, affiché sur l'extérieur du radar
     * @property {String} skillCompletName - Nom long de la compétence, affiché au survol au centre du radar
     * @property {Array|Number[]} values - Valeurs en fonction de l'itération
     * @return {skillData[]}
     */
    async generateRadarData () {
      let data = []

      // Initie les itérations
      this.settings.iteration.current = this.results[0].skills[0].results.length
      this.settings.iteration.end = this.results[0].skills[0].results.length
      // Initie le point de départ du slider une fois l'itération actuelle définie
      if (this.resultNumber > 1) {
        this.sliders.iteration.start = this.settings.iteration.current
      }
      // Initie la valeur de départ en Y une fois l'itération actuelle définie
      this.series.dataFields.valueY = 'value' + this.settings.iteration.current

      // Initie le texte central une fois l'itération actuelle définie
      this.chart.yAxis.label.text = this.finishedIterations[this.finishedIterations.length - 1]
        .finishedAt

      // Parcours le tableau des résultats
      this.results.forEach((domain, i) => {
        /**
         * Parcours le tableau des compétences pour en extraire :
         * - le nom de la compétence
         * - les différents résultats
         */
        domain.skills.forEach((skill, j) => {
          skill.shortName = skill.shortName === '' ? `C${i + 1}.${j + 1}` : skill.shortName

          let skillResults = {
            skillShortName: skill.shortName,
            skillCompletName: skill.name
          }

          // Parcours le tableau des résultats
          skill.results.forEach((result, index) => {
            skillResults['value' + (this.settings.iteration.start + index)] = result
          })

          data.push(skillResults)
        })

        this.createRange(domain, i)
      })

      return data
    },
    /**
     * Crée les différents parties du radar, délimités par les domaines.
     * On crée une section pour chaque domaine.
     *
     * @param domain {Results}
     * @param index {Number}
     */
    createRange (domain, index) {
      /** Initie la section */
      let axisRange = this.chart.xAxis.axis.axisRanges.create()
      axisRange.axisFill.interactionsEnabled = true
      /** Première compétence */
      axisRange.category = domain.skills[0].shortName
      /** Dernière compétence */
      axisRange.endCategory = domain.skills[domain.skills.length - 1].shortName

      /** Mise en forme avec une gestion 3D des couleurs de fond */
      axisRange.axisFill.fill = this.colorSet.getIndex(index * 4)
      axisRange.grid.disabled = true
      axisRange.label.interactionsEnabled = false
      /** N'affiche pas le label car pas zoomé **/
      axisRange.text = ''
      axisRange.label.bent = true
      axisRange.label.fill = this.brightnessColorCalcul(axisRange.axisFill.fill) >= 150
        ? am4core.color('#111111')
        : am4core.color('#ffffff')
      axisRange.label.radius = 3

      /** Mise en forme du fond des barres de domaine */
      let axisFill = axisRange.axisFill
      axisFill.innerRadius = -0.001 // almost the same as 100%, we set it in pixels as later we animate this property to some pixel value
      axisFill.radius = -20 // negative radius means it is calculated from max radius
      axisFill.disabled = false // as regular fills are disabled, we need to enable this one
      axisFill.fillOpacity = 1
      axisFill.togglable = true

      /** Mise en place de la fenêtre d'information au survol avec le nom du domaine */
      axisFill.showSystemTooltip = true
      axisFill.readerTitle = `${domain.name}`
      axisFill.cursorOverStyle = am4core.MouseCursorStyle.pointer

      /** Met en place le zoom quand on clique sur une catégorie */
      axisFill.events.on('hit', event => {
        let dataItem = event.target.dataItem

        if (!event.target.isActive) {
          this.chart.xAxis.axis.zoom({
            start: 0,
            end: 1
          })

          /** Désactive le label quand on dézoom **/
          axisRange.text = ''
        } else {
          this.chart.xAxis.axis.zoomToCategories(dataItem.category, dataItem.endCategory)

          /** Active le label quand on zoom **/
          axisRange.text = domain.name
        }
      })

      /** Crée l'effet de zoom sur la barre du domaine au survol */
      let hoverState = axisFill.states.create('hover')
      hoverState.properties.innerRadius = -10
      hoverState.properties.radius = -25
    },
    /**
     * Calcule un ratio de luminosité, et permet de choisir la couleur de la police en fonction
     * @param color {Object}
     * @return {number}
     */
    brightnessColorCalcul (color) {
      return ((color.rgb.r * 299) + (color.rgb.g * 587) + (color.rgb.b * 144)) / 1000
    }
  }
}
</script>
